import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Form } from '@unform/web';
import * as Yup from 'yup';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import PHE from 'print-html-element';
import Autocomplete from '~/styles/components/Autocomplete';
import AutocompleteMultiple from '~/styles/components/AutocompleteMultiple';
import PatientData from './components/PatientData';
import Input from '~/styles/components/Input';
import DatePicker from '~/styles/components/DatePicker';
import Backdrop from '~/components/BackdropComponent';
import InputLabel from '~/components/InputLabel';
import SimpleButton from '~/styles/components/SimpleButton';
import Days from './components/Days';
import AlertDialog from './components/AlertDialog';
import Ticket from './components/Ticket';
import SubTitle from '~/components/SubTitle';
import { errorMessage } from '~/utils/Message';

import { Container, SectionTitle } from './styles';

import api from '~/services/api';
import { BoxValue } from './components/PatientData/styles';

const recommendationData = [
  { label: 'Profilaxia Primária' },
  { label: 'Profilaxia Secundária' },
  { label: 'Profilaxia Terciária' },
  { label: 'Imunotolerância' },
  { label: 'Sob Demanda' },
];

const presentationData = [
  '200UI',
  '250UI',
  '500UI',
  '600UI',
  '1000UI',
  '1200UI',
  '2500UI',
];

const dispensationMotiveData = [
  'Hermatrose',
  'Cirurgia eletiva',
  'Hematoma',
  'Trat. dentário',
  'Trauma',
  'Sang. mucosa',
  'Sang. intracran.',
  'Fisioterapia',
];

function Pharmacy() {
  const profile = useSelector((state) => state.user.profile);
  const [name, surname] = profile.name.split(' ');

  const hemocenter_id = profile.hemocenter ? profile.hemocenter.id : undefined;

  const [patients, setPatients] = useState([]);
  const { state } = useLocation();
  const [selectedPatient, setSelectedPatient] = useState(
    state ? state.user : null
  ); // selected patient from local component or history.push with state

  const [factors, setFactors] = useState([]);
  const [selectedFactor, setSelectedFactor] = useState(null);

  const [manufacturers, setManufacturers] = useState([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);

  const formRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  const loadPatients = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await api.get('/patients', {
        params: {
          hemocenter_id,
          pageSize: Number.MAX_SAFE_INTEGER,
        },
      });

      setPatients(data.patients);
    } catch (error) {
      errorMessage(error);
    } finally {
      setLoading(false);
    }
  }, [hemocenter_id]);

  async function loadFactors(manufacturer = undefined) {
    // setLoading(true);

    try {
      const { data } = await api.get('/factors', {
        params: {
          manufacturer,
        },
      });

      setFactors(data);
    } finally {
      // setLoading(false);
    }
  }

  async function loadManufacturers(factor = undefined) {
    // setLoading(true);

    try {
      const { data } = await api.get('/manufacturers', {
        params: {
          factor,
        },
      });

      setManufacturers(data);
    } finally {
      // setLoading(false);
    }
  }

  async function handleSubmit(data, { reset }) {
    setLoading(true);

    try {
      const schema = Yup.object({
        patient_id: Yup.string().required('Campo obrigatório.'),
        factor: Yup.string().required('Campo obrigatório.'),
        manufacturer: Yup.string().required('Campo obrigatório.'),
        quantity: Yup.string().required('Campo obrigatório.'),
        days: Yup.array().required('Campo obrigatório.'),
        recommendation: Yup.string().nullable().required('Campo obrigatório.'),
        validity: Yup.string().nullable(),
        lot: Yup.string(),
        received_by: Yup.string(),
      });

      await schema.validate(data, {
        abortEarly: false,
      });
      // Validation passed

      await api.post('/pharmacy', {
        ...data,
        patient_id: selectedPatient.id,
        hemocenter_id,
      });

      setModalVisible(true);
      // toast.success('Sucesso, registramos o medicamento.');
    } catch (err) {
      const validationErrors = {};
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });
        formRef.current.setErrors(validationErrors);
      } else {
        errorMessage(err);
      }
    } finally {
      setLoading(false);
    }
  }

  /**
   * Prints an html element.
   */
  function print() {
    const contents = document.getElementById('ticket');
    PHE.printElement(contents);
  }

  useEffect(() => {
    document.title = 'Registro de medicamento | ClickHemofilia';
  }, []);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  // Updates manufacturer list when factor is changed.
  useEffect(() => {
    loadManufacturers(selectedFactor);
  }, [selectedFactor]);

  // Updates factors list when manufacturer is changed.
  useEffect(() => {
    loadFactors(selectedManufacturer);
  }, [selectedManufacturer]);

  return (
    <Container>
      <div className="header">
        <SubTitle>Registro de medicamentos</SubTitle>
        <InputLabel>*Campos obrigatórios</InputLabel>
      </div>

      <Form onSubmit={handleSubmit} ref={formRef} className="form">
        <SectionTitle className="section-title">
          Informações do paciente
        </SectionTitle>

        {/* Patient information section */}
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Autocomplete
              name="patient_id"
              label="Nome completo do paciente*"
              data={patients}
              state={selectedPatient}
              setState={setSelectedPatient}
              getOptionLabel={(option) => option.name}
            />
          </Grid>

          <Grid item xs={6}>
            <PatientData patient={selectedPatient} />
          </Grid>
        </Grid>

        <SectionTitle className="section-title">
          Registro de medicamento
        </SectionTitle>

        {/* Drug information section */}
        <Grid container spacing={3}>
          {/* Line 01 */}
          <>
            <Grid item xs={6}>
              <Autocomplete
                name="factor"
                label="Concentrado de fator*"
                data={factors}
                setState={(value) =>
                  setSelectedFactor(value ? value.factor : null)
                }
                getOptionLabel={(option) => option.factor}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                name="manufacturer"
                label="Fabricante*"
                data={manufacturers}
                setState={(value) =>
                  setSelectedManufacturer(value ? value.manufacturer : null)
                }
                getOptionLabel={(option) => option.manufacturer}
              />
            </Grid>
          </>

          {/* Line 02 */}
          <>
            <Grid item xs={2}>
              <Input label="Número de doses*" name="quantity" type="number" />
            </Grid>
            <Grid item xs={2}>
              <DatePicker
                label="Validade"
                name="validity"
                disablePast
                placeholder="dd/mm/yyyy"
              />
            </Grid>
            <Grid item xs={2}>
              <Input label="Lote" name="lot" />
            </Grid>
            <Grid item xs={6}>
              <Days name="days" />
            </Grid>
          </>

          {/* Line 03 */}
          <>
            <Grid item xs={6}>
              <Autocomplete
                name="recommendation"
                label="Indicação*"
                data={recommendationData}
                placeholder="Profilaxia Primária"
                getOptionLabel={(option) => option.label}
              />
            </Grid>
            <Grid item xs={3}>
              <AutocompleteMultiple
                name="presentation"
                label="Apresentação"
                data={presentationData}
              />
            </Grid>
            <Grid item xs={3}>
              <AutocompleteMultiple
                name="motive_dispensation"
                label="Motivo da dispensa"
                data={dispensationMotiveData}
              />
            </Grid>
          </>

          {/* Line 04 */}
          <>
            <Grid item xs={6}>
              <Input label="Recebido por" name="received_by" />
            </Grid>

            <Grid item xs={6}>
              <InputLabel>Dispensado por</InputLabel>
              <BoxValue id="dispensed-by">
                {surname ? `${name} ${surname}` : name}
              </BoxValue>
            </Grid>
          </>
        </Grid>

        <SimpleButton type="submit">Cadastrar</SimpleButton>
      </Form>

      <AlertDialog
        open={modalVisible}
        setOpen={setModalVisible}
        title="Deseja imprimir?"
        submitButton={print}
        cancelButton={() => formRef.current.reset()}
      >
        <Ticket
          data={() => formRef.current.getData()}
          dispensed_by={surname ? `${name} ${surname}` : name}
        />
      </AlertDialog>

      <Backdrop open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
}

export default Pharmacy;
